import { MutableRefObject, ReactElement, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Link, Text } from '@opendoor/bricks-next';

import { EntryComponent } from 'cms/entries/entries';

import Container from 'components/landing-pages-v2/shared/Container';

import { ITrustpilotWidget } from 'declarations/contentful';

// to override widgets, at a minimum, set template-id and both data-token-*
//
// see https://businessapp.b2b.trustpilot.com/trustbox/
// may require explicit auth via OD marketing admin
export type TPCommonData = {
  'data-businessunit-id': string; // eg '5972843f0000ff0005a7274a'
  'data-locale': string; // eg 'en-US'
  'data-style-width': string; // eg '100%'
  'data-template-id': string; // eg '53aa8912dec7e10d38f59f36'
  'data-token-desktop': string; // UUID
  'data-token-mobile': string; // UUID
} & Partial<{
  'data-stars': string; // eg '4,5'
  'data-background': string; // eg 'none',
  'data-external-elements-color': string; // eg 'dark',
  'data-headline': string; // eg 'star',
  'data-review-languages': string; // en
  'data-style-height': string; // eg '140px' eg 'en'
  'data-style-size': string; // eg 'XS',
  'data-text-color': string; // eg 'dark',
}>;
export interface IRenderTPWidget extends ITrustpilotWidget {
  rawWidget?: boolean;
  tpCommonDataOverride?: Partial<TPCommonData>;
  hideFallback?: boolean;
}

const TPLink = ({ href = 'https://www.trustpilot.com/review/opendoor.com' }: { href?: string }) => (
  <Link
    href={href}
    target="_blank"
    rel="noopener"
    analyticsName="cosmos-landing-page-v2-trustpilot-link"
  >
    Trustpilot
  </Link>
);

const TPWidget = ({
  desktopContainerRef,
  mobileContainerRef,
  trustpilotCommonData,
  hideFallback = false,
}: {
  desktopContainerRef: MutableRefObject<HTMLDivElement | null>;
  mobileContainerRef: MutableRefObject<HTMLDivElement | null>;
  trustpilotCommonData: TPCommonData;
  hideFallback?: boolean;
}) => (
  <>
    <Box
      ref={desktopContainerRef}
      className="trustpilot-widget"
      {...trustpilotCommonData}
      data-token={trustpilotCommonData['data-token-desktop']}
      $smallerThanMD={{ display: 'none' }}
    >
      {hideFallback ? null : <TPLink />}
    </Box>
    <Box
      ref={mobileContainerRef}
      className="trustpilot-widget"
      {...trustpilotCommonData}
      data-token={trustpilotCommonData['data-token-mobile']}
      display="none"
      $smallerThanMD={{ display: 'block' }}
    >
      {hideFallback ? null : <TPLink />}
    </Box>
  </>
);

const TPRawClientWrapper = ({ children }: { children: ReactElement }) => {
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);
  return isClient ? children : null;
};

export const RenderTrustpilotWidget = (params: IRenderTPWidget) => {
  const { title } = params.fields;
  const desktopContainerRef = useRef<HTMLDivElement | null>(null);
  const mobileContainerRef = useRef<HTMLDivElement | null>(null);

  const trustpilotCommonData: TPCommonData = useMemo(
    () => ({
      'data-locale': 'en-US',
      'data-template-id': '53aa8912dec7e10d38f59f36',
      'data-businessunit-id': '5972843f0000ff0005a7274a',
      'data-style-height': '140px',
      'data-style-width': '100%',
      'data-stars': '4,5',
      'data-review-languages': 'en',
      'data-token-desktop': 'f5ac60d1-d0ba-4154-a67a-3b5d7d6f2e28',
      'data-token-mobile': '9361d9bc-b89c-41fd-ad2e-8daafedba526',
      ...params.tpCommonDataOverride,
    }),
    [params.tpCommonDataOverride],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const tryLoad = () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const w = window as any;
      let didAttempt = false;
      if (w?.Trustpilot?.loadFromElement) {
        if (desktopContainerRef.current) {
          w.Trustpilot.loadFromElement(desktopContainerRef.current);
          didAttempt = true;
        }
        if (mobileContainerRef.current) {
          w.Trustpilot.loadFromElement(mobileContainerRef.current);
          didAttempt = true;
        }
      }
      return didAttempt;
    };

    if (tryLoad()) return;

    const intervalId = window.setInterval(() => {
      if (tryLoad()) {
        window.clearInterval(intervalId);
      }
    }, 500);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  if (params.rawWidget) {
    return (
      <TPRawClientWrapper>
        <TPWidget
          desktopContainerRef={desktopContainerRef}
          mobileContainerRef={mobileContainerRef}
          trustpilotCommonData={trustpilotCommonData}
          hideFallback={params?.hideFallback}
        />
      </TPRawClientWrapper>
    );
  }

  return (
    <Box>
      <Container backgroundColor="$backgroundSecondary">
        {title && (
          <Box py="$16x">
            <Text
              tag="span"
              display="block"
              typography="$displaySmall"
              $smallerThanMD={{ typography: '$headerSmall' }}
            >
              {title}
            </Text>
          </Box>
        )}
        <Box pb="$16x">
          <TPWidget
            desktopContainerRef={desktopContainerRef}
            mobileContainerRef={mobileContainerRef}
            trustpilotCommonData={trustpilotCommonData}
            hideFallback={params?.hideFallback}
          />
        </Box>
      </Container>
    </Box>
  );
};

const TrustpilotWidget: EntryComponent<ITrustpilotWidget> = {
  render: RenderTrustpilotWidget,
};

export default TrustpilotWidget;
